import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse, Piece, PieceAlias, PieceRegion, RepertoireEntry, Arrangement, PracticeHistoryEntry, Comment, Genre } from '@/lib/types';
import type { ContributePiecePayload } from '@/lib/contribute-form-payload';

type CreatePieceRequest = {
  prettyName: string;
  canonicalName: string;
  composerId?: string;
};

type CreateArrangementRequest = {
  prettyName: string;
  canonicalName: string;
  keyId?: string;
  formId?: string;
  timeSignatureId?: string;
};

type ArrangementResponse = {
  arrangementId: string;
  prettyName: string;
  canonicalName: string;
  keyId: string | null;
  formId: string | null;
};

type AddToRepertoireRequest = {
  arrangementId: string;
  queueStatusId: string;
  masteryLevelId?: string;
  keyId?: string;
  practiceTempoBeats?: number;
  performanceTempoBeats?: number;
  notes?: string | null;
  firstPracticeOn?: string | null;
};

export type PieceDuplicateMatch = {
  pieceId: string;
  prettyName: string;
  canonicalName: string;
  composerId: string | null;
  composerName: string | null;
  forms: string[];
  arrangementCount: number;
  similarity: number;
};

const DUPLICATE_MIN_TITLE_LENGTH = 3;
const DUPLICATE_DEFAULT_LIMIT = 3;

export function usePieceDuplicates(
  title: string,
): ReturnType<typeof useQuery<PieceDuplicateMatch[]>> {
  const trimmed = title.trim();
  return useQuery({
    queryKey: ['pieces', 'duplicates', { title }] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('title', title);
      params.set('limit', String(DUPLICATE_DEFAULT_LIMIT));
      return api.fetch<PieceDuplicateMatch[]>(`/pieces/duplicates?${params}`);
    },
    enabled: trimmed.length >= DUPLICATE_MIN_TITLE_LENGTH,
  });
}

export function useSearchPieces(search: string): ReturnType<typeof useQuery<PageResponse<Piece>>> {
  return useQuery({
    queryKey: ['pieces', { search }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('size', '20');
      return api.fetch<PageResponse<Piece>>(`/pieces?${params}`);
    },
    enabled: search.length >= 2,
  });
}

const SEARCH_PAGE_SIZE = 20;

export function useSearchPiecesInfinite(search: string): ReturnType<
  typeof useInfiniteQuery<PageResponse<Piece>, Error, { pages: PageResponse<Piece>[]; pageParams: number[] }, ['pieces', 'infinite', { search: string }], number>
> {
  return useInfiniteQuery({
    queryKey: ['pieces', 'infinite', { search }] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', String(pageParam));
      params.set('size', String(SEARCH_PAGE_SIZE));
      return api.fetch<PageResponse<Piece>>(`/pieces?${params}`);
    },
    getNextPageParam: (lastPage) =>
      lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: search.length >= 2,
  });
}

const ALL_PIECES_PAGE_SIZE = 50;

export function useAllPiecesInfinite(search: string): ReturnType<
  typeof useInfiniteQuery<PageResponse<Piece>, Error, { pages: PageResponse<Piece>[]; pageParams: number[] }, ['pieces', 'all', 'infinite', { search: string }], number>
> {
  return useInfiniteQuery({
    queryKey: ['pieces', 'all', 'infinite', { search }] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', String(pageParam));
      params.set('size', String(ALL_PIECES_PAGE_SIZE));
      return api.fetch<PageResponse<Piece>>(`/pieces?${params}`);
    },
    getNextPageParam: (lastPage) =>
      lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
  });
}

export function usePieceArrangements(pieceId: string): ReturnType<typeof useQuery<PageResponse<Arrangement>>> {
  return useQuery({
    queryKey: ['pieces', pieceId, 'arrangements'],
    queryFn: () => api.fetch<PageResponse<Arrangement>>(`/pieces/${pieceId}/arrangements?size=100`),
    enabled: Boolean(pieceId),
  });
}

export function useCreatePiece(): ReturnType<typeof useMutation<Piece, Error, CreatePieceRequest>> {
  return useMutation({
    mutationFn: (data: CreatePieceRequest) =>
      api.fetch<Piece>('/pieces', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export type ContributeArrangementSummary = {
  arrangementId: string;
  primary: boolean;
};

export type ContributePieceResponse = {
  pieceId: string;
  status: 'PUBLISHED';
  title: string;
  forms: string[];
  arrangements: ContributeArrangementSummary[];
};

export function useContributePiece(): ReturnType<
  typeof useMutation<ContributePieceResponse, Error, ContributePiecePayload>
> {
  return useMutation({
    mutationFn: (payload: ContributePiecePayload) =>
      api.fetch<ContributePieceResponse>('/pieces/contribute', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  });
}

export function useCreateArrangement(): ReturnType<
  typeof useMutation<ArrangementResponse, Error, { pieceId: string } & CreateArrangementRequest>
> {
  return useMutation({
    mutationFn: ({ pieceId, ...data }: { pieceId: string } & CreateArrangementRequest) =>
      api.fetch<ArrangementResponse>(`/pieces/${pieceId}/arrangements`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function usePiece(pieceId: string): ReturnType<typeof useQuery<Piece>> {
  return useQuery({
    queryKey: ['pieces', pieceId],
    queryFn: () => api.fetch<Piece>(`/pieces/${pieceId}`),
    enabled: Boolean(pieceId),
  });
}

export function usePieceAliases(pieceId: string): ReturnType<typeof useQuery<PieceAlias[]>> {
  return useQuery({
    queryKey: ['pieces', pieceId, 'aliases'],
    queryFn: () => api.fetch<PieceAlias[]>(`/pieces/${pieceId}/aliases`),
    enabled: Boolean(pieceId),
  });
}

export function useArrangement(arrangementId: string | undefined): ReturnType<typeof useQuery<Arrangement>> {
  return useQuery({
    queryKey: ['arrangements', arrangementId],
    queryFn: () => api.fetch<Arrangement>(`/arrangements/${arrangementId}`),
    enabled: Boolean(arrangementId),
  });
}

type UpdatePieceRequest = {
  pieceId: string;
  prettyName?: string;
  canonicalName?: string;
  composerId?: string | null;
  forms?: string[];
  region?: PieceRegion | null;
  summary?: string | null;
  aka?: string[];
};
export function useUpdatePiece(): ReturnType<typeof useMutation<Piece, Error, UpdatePieceRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, ...data }: UpdatePieceRequest) =>
      api.fetch<Piece>(`/pieces/${pieceId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['pieces', variables.pieceId] });
      void queryClient.invalidateQueries({ queryKey: ['pieces', 'all'] });
    },
  });
}

type UpdateArrangementRequest = { arrangementId: string; prettyName?: string; canonicalName?: string; keyId?: string | null; formId?: string | null; timeSignatureId?: string | null };
export function useUpdateArrangement(): ReturnType<typeof useMutation<Arrangement, Error, UpdateArrangementRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, ...data }: UpdateArrangementRequest) =>
      api.fetch<Arrangement>(`/arrangements/${arrangementId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['pieces'] }); },
  });
}

export function useArrangementGenres(arrangementId: string): ReturnType<typeof useQuery<Genre[]>> {
  return useQuery({
    queryKey: ['arrangements', arrangementId, 'genres'],
    queryFn: () => api.fetch<Genre[]>(`/arrangements/${arrangementId}/genres`),
    enabled: Boolean(arrangementId),
  });
}

export function useAddArrangementGenre(): ReturnType<typeof useMutation<Genre, Error, { arrangementId: string; genreId: string }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, genreId }) =>
      api.fetch<Genre>(`/arrangements/${arrangementId}/genres`, { method: 'POST', body: JSON.stringify({ genreId }) }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'genres'] });
    },
  });
}

export function useRemoveArrangementGenre(): ReturnType<typeof useMutation<void, Error, { arrangementId: string; genreId: string }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, genreId }) =>
      api.fetch<void>(`/arrangements/${arrangementId}/genres/${genreId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'genres'] });
    },
  });
}

export function useArrangementComments(arrangementId: string): ReturnType<typeof useQuery<Comment[]>> {
  return useQuery({
    queryKey: ['arrangements', arrangementId, 'comments'],
    queryFn: () => api.fetch<Comment[]>(`/arrangements/${arrangementId}/comments`),
    enabled: Boolean(arrangementId),
  });
}

export function useAddComment(): ReturnType<typeof useMutation<Comment, Error, { arrangementId: string; content: string }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, content }) =>
      api.fetch<Comment>(`/arrangements/${arrangementId}/comments`, { method: 'POST', body: JSON.stringify({ content }) }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'comments'] });
    },
  });
}

export function useDeleteComment(): ReturnType<typeof useMutation<void, Error, { arrangementId: string; commentId: string }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, commentId }) =>
      api.fetch<void>(`/arrangements/${arrangementId}/comments/${commentId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'comments'] });
    },
  });
}

export function usePracticeHistory(arrangementId: string): ReturnType<typeof useQuery<PracticeHistoryEntry[]>> {
  return useQuery({
    queryKey: ['practice-history', arrangementId],
    queryFn: () => api.fetch<PracticeHistoryEntry[]>(`/practice-sessions/by-arrangement/${arrangementId}`),
    enabled: Boolean(arrangementId),
  });
}

export function useAddToRepertoire(): ReturnType<typeof useMutation<RepertoireEntry, Error, AddToRepertoireRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddToRepertoireRequest) =>
      api.fetch<RepertoireEntry>('/repertoire', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repertoire'] });
      void queryClient.invalidateQueries({ queryKey: ['practice-queue'] });
    },
  });
}
